// apps/web/src/pages/onboarding/PendingVerificationPage.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck, CheckCircle, Package, Warehouse, ShieldCheck,
  Clock, FileCheck, User, Mail, Phone, AlertCircle, Sparkles
} from 'lucide-react';
import { useSocket } from '@/hooks/useSocket';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

const clientTips = [
  "While you wait, prepare details for your first job posting.",
  "Verified clients gain access to our full network of trusted transporters.",
  "Ensure your contact details are up-to-date for smooth communication.",
  "Average verification time: 2-5 minutes during business hours.",
  "Your documents are encrypted and securely stored."
];

const transporterTips = [
  "Clear, well-lit photos help us verify your vehicles faster.",
  "Verified transporters unlock access to exclusive, high-value jobs.",
  "Get ready to browse the job board as soon as you are verified.",
  "Most verifications complete within 5 minutes.",
  "Verified status builds trust with clients and increases bookings."
];

const AnimatedGrid = () => (
  <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
    <motion.div
      className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"
      animate={{ backgroundPosition: ["0 0", "24px 24px"] }}
      transition={{ duration: 5, ease: "linear", repeat: Infinity }}
    />
    {/* Floating particles */}
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-primary/20 rounded-full"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          y: [0, -30, 0],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 3 + Math.random() * 2,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      />
    ))}
  </div>
);

const StepIndicator = ({ status }: { status: 'PENDING' | 'VERIFYING' | 'VERIFIED' }) => {
  const steps = [
    { label: 'Submitted', icon: FileCheck },
    { label: 'Under Review', icon: Clock },
    { label: 'Verified', icon: CheckCircle }
  ];
  const currentStep = status === 'PENDING' ? 1 : status === 'VERIFYING' ? 2 : 3;

  return (
    <div className="relative pt-6 pb-4">
      <div className="absolute top-9 left-12 right-12 h-1 bg-border rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-primary to-primary/80"
          initial={{ width: "0%" }}
          animate={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        />
      </div>
      <div className="flex justify-between relative">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isActive = idx + 1 <= currentStep;
          const isCurrent = idx + 1 === currentStep;

          return (
            <div key={step.label} className="flex flex-col items-center gap-2">
              <motion.div
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${isActive
                    ? 'bg-primary border-primary text-primary-foreground shadow-lg shadow-primary/50'
                    : 'bg-card border-border'
                  }`}
                animate={isCurrent ? { scale: [1, 1.1, 1] } : {}}
                transition={{ repeat: isCurrent ? Infinity : 0, duration: 2 }}
              >
                <Icon className="w-5 h-5" />
              </motion.div>
              <span className={`text-xs font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PendingVerificationPage = () => {
  const navigate = useNavigate();
  const { user, setVerified } = useAuthStore();
  const [tipIndex, setTipIndex] = useState(0);
  const [status, setStatus] = useState<'PENDING' | 'VERIFYING' | 'VERIFIED'>('PENDING');
  const tips = user?.role === 'TRANSPORTER' ? transporterTips : clientTips;
  const utils = trpc.useUtils();

  // Poll for verification status
  trpc.user.getVerificationStatus.useQuery(undefined, {
    refetchInterval: (data) => (data?.status === 'VERIFIED' ? false : 5000),
    enabled: user?.verificationStatus === 'PENDING' && status !== 'VERIFIED',
    onSuccess: (data) => {
      if (data?.status === 'VERIFIED') {
        console.log('✅ Verification detected via polling');
        setStatus('VERIFYING');
      }
    }
  });

  // Listen for real-time verification via socket
  const { socket } = useSocket();
  useEffect(() => {
    const onVerificationSuccess = (data: { message: string }) => {
      console.log('✅ Verification detected via socket:', data);
      setStatus('VERIFIED');
    };

    socket?.on('ACCOUNT_VERIFIED', onVerificationSuccess);
    return () => {
      socket?.off('ACCOUNT_VERIFIED', onVerificationSuccess);
    };
  }, [socket]);

  // Handle status transitions
  useEffect(() => {
    if (status === 'VERIFYING') {
      const timer = setTimeout(() => {
        setStatus('VERIFIED');
      }, 500); // Fast transition
      return () => clearTimeout(timer);
    }

    if (status === 'VERIFIED') {
      toast.success('Account Verified. Welcome aboard.', { duration: 3000 });
      setVerified();
      utils.user.getProfile.invalidate();

      const timer = setTimeout(() => {
        console.log('Redirecting to dashboard...');
        window.location.href = '/dashboard';
      }, 500); // Immediate redirect

      return () => clearTimeout(timer);
    }
  }, [status, setVerified, utils, navigate]);

  // Tip rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex((i) => (i + 1) % tips.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [tips.length]);

  // If user is already verified, redirect immediately
  useEffect(() => {
    if (user?.verificationStatus === 'VERIFIED') {
      console.log('User already verified, redirecting...');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const headline = status === 'VERIFIED'
    ? "Verification Complete"
    : "Your Account is Under Review";

  const description = status === 'VERIFIED'
    ? `Welcome, ${user?.profile?.fullName}! You now have full access to the platform.`
    : `Our team is reviewing your documents. This usually takes 2-5 minutes.`;

  return (
    <div className="relative flex items-center justify-center min-h-[calc(100vh-80px)] bg-background overflow-hidden p-4">
      <AnimatedGrid />

      <motion.div
        className="relative z-10 w-full max-w-2xl space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Main Card */}
        <Card className="shadow-2xl border-2">
          <CardContent className="p-8 space-y-6">
            {/* Animated truck illustration */}
            <div className="overflow-hidden relative h-32 bg-gradient-to-r from-primary/10 via-primary/5 to-secondary/10 rounded-xl border-2 border-dashed border-primary/20 flex items-center justify-between px-8">
              <Warehouse className="w-14 h-14 text-primary/40" />
              <motion.div
                className="absolute top-1/2 -translate-y-1/2 left-0"
                initial={{ x: '10%' }}
                animate={{ x: status === 'VERIFIED' ? '82%' : '45%' }}
                transition={{ duration: 1.5, ease: 'easeInOut' }}
              >
                <div className="relative">
                  <Truck className="w-24 h-24 text-primary drop-shadow-lg" />
                  {status === 'VERIFYING' && (
                    <motion.div
                      className="absolute -top-2 -right-2"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Sparkles className="w-6 h-6 text-yellow-500" />
                    </motion.div>
                  )}
                </div>
              </motion.div>
              <ShieldCheck className={`w-14 h-14 transition-all duration-500 ${status === 'VERIFIED' ? 'text-green-500 scale-110' : 'text-primary/40'
                }`} />
            </div>

            {/* Headline */}
            <div className="text-center space-y-3 pt-2">
              <AnimatePresence mode="wait">
                <motion.h1
                  key={headline}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -10, opacity: 0 }}
                  className="text-3xl font-bold text-foreground"
                >
                  {headline}
                </motion.h1>
              </AnimatePresence>
              <p className="text-muted-foreground text-lg">{description}</p>

              {status === 'PENDING' && (
                <Badge variant="outline" className="mt-2">
                  <Clock className="w-3 h-3 mr-1" />
                  Estimated wait: 2-5 minutes
                </Badge>
              )}
            </div>

            {/* Step indicator */}
            <StepIndicator status={status} />

            {/* Rotating tips */}
            <div className="pt-2">
              <Card className="bg-muted/50 border-primary/20">
                <CardContent className="p-4">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={tipIndex}
                      className="text-sm text-foreground font-medium text-center min-h-[3rem] flex items-center justify-center"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      {tips[tipIndex]}
                    </motion.p>
                  </AnimatePresence>
                </CardContent>
              </Card>
            </div>

            {/* Action button */}
            {status === 'VERIFIED' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Button asChild className="w-full h-12 text-lg" size="lg">
                  <Link to="/dashboard">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Go to Dashboard
                  </Link>
                </Button>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* User Info Card */}
        <Card className="bg-card/50 backdrop-blur border">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
              <User className="w-4 h-4" />
              Submitted Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Name:</span>
                <span className="font-medium">{user?.profile?.fullName || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Email:</span>
                <span className="font-medium">{user?.email || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Role:</span>
                <Badge variant="secondary">{user?.role || 'N/A'}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Status:</span>
                <Badge variant="outline" className="border-primary/50 text-primary">
                  Under Review
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card className="bg-amber-500/10 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                  Taking longer than expected?
                </p>
                <p className="text-amber-800 dark:text-amber-200">
                  Contact support at <a href="mailto:support@kclogistics.com" className="underline font-medium">support@kclogistics.com</a> or call <a href="tel:+265123456789" className="underline font-medium">+265 123 456 789</a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PendingVerificationPage;
